import React, { useEffect, useState } from 'react';
import { Select, Skeleton, Tag } from 'antd';
import {
	useLazyGetMaterialReservationByIdQuery,
	useUpdateMaterialReservationCutStatusMutation,
} from '../../redux/api/material';
import { MaterialReservation } from '../../interfaces/MaterialInventory.interface';
import { getTagColor } from '../../utils/index.ts';
import CutMaterialModal from '../Operations/SawCutQueue/SawCutQueueTable/CutMaterialModal';
import { useNavigate } from 'react-router';
import { useGetLoggedInUserQuery } from '../../redux/api/users.ts';

interface Props {
	reservation: MaterialReservation;
	refetch: () => void;
}

interface CutMaterialReservationProps {
	material_reservation_id: string;
	setIsCutModalOpen: (value: boolean) => void;
	refetch: () => void;
}

const CutReservation = ({
	material_reservation_id,
	setIsCutModalOpen,
	refetch,
}: CutMaterialReservationProps) => {
	const [loading, setLoading] = useState(false);
	const { data: user, isLoading, error } = useGetLoggedInUserQuery();
	const [getMaterialReservationById] = useLazyGetMaterialReservationByIdQuery();
	const [materialReservationData, setMaterialReservationData] = useState<MaterialReservation | null>(null);

	useEffect(() => {
		setLoading(true);
		if (material_reservation_id) {
			getMaterialReservationById({
				id: material_reservation_id,
			})
				.then((response) => {
					if (response.data) {
						setMaterialReservationData(response.data);
					}
				})
				.catch((error) => {});
		}
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [material_reservation_id]);

	const reservations = materialReservationData ? [materialReservationData] : [];

	if (loading || !materialReservationData) {
		return null;
	}

	return (
		<>
			<CutMaterialModal
				visible={true}
				onCancel={() => {
					setIsCutModalOpen(false);
					refetch();
				}}
				materialReservations={reservations}
				overrideMaterialReservations={(reservations) => {
					setMaterialReservationData(reservations[0]);
				}}
				loadingMaterialReservations={false}
				refetch={() => {
					refetch();
				}}
				defaultUser={user?.id}
			/>
		</>
	);
};

export const MaterialReservationStatus: React.FC<Props> = ({
	reservation,
	refetch,
}) => {
	const [isCutModalOpen, setIsCutModalOpen] = useState(false);
	const [selectedStatus, setSelectedStatus] = useState<string>(
		reservation.material_cut_status
	);

	useEffect(() => {
		setSelectedStatus(reservation.material_cut_status);
	}, [reservation.material_cut_status]);

	const [updateMaterialReservationCutStatus] =
		useUpdateMaterialReservationCutStatusMutation();

	/**
	 * This will be called if the user selects a status other than 'Cut',
	 * or after the user submits the modal for 'Cut'.
	 */
	async function handleUpdateMaterialReservationStatus(
		value: string,
		length?: number
	) {
		try {
			await updateMaterialReservationCutStatus({
				id: reservation.id,
				values: {
					material_cut_status: value,
					current_length: length,
				},
			}).unwrap();
			refetch();
		} catch (error) {
			console.error('Failed to update material reservation:', error);
		}
	}

	/**
	 * Triggered whenever the user selects a new status from the <Select> dropdown.
	 */
	const handleSelectStatus = (value: string, e) => {
		// If the user selects 'Cut' AND it's not already cut, open the modal

		if (value === 'Cut') {
			setIsCutModalOpen(true);
			return false;
		} else {
			// Directly update status if user chooses something else
			setSelectedStatus(value);
			handleUpdateMaterialReservationStatus(value);
		}
	};

	return (
		<>
			<Select
				data-testid="fastlane-material-reservation-cut-status"
				data-material-reservation-id={reservation.id}
				data-material-cut-status={selectedStatus}
				value={selectedStatus}
				className="flex"
				size="small"
				variant="borderless"
				disabled={reservation.material_cut_status === 'Cut'}
				onChange={handleSelectStatus}
				style={{ width: 140 }}
				popupMatchSelectWidth={false}
				labelRender={({ label, value }) => (
					<Tag color={getTagColor(value?.toLocaleString())}>{label}</Tag>
				)}
				options={['Cut', 'In Progress', 'Not Cut'].map((x) => ({
					value: x,
					label: x,
				}))}
			/>

			{isCutModalOpen ? (
				<CutReservation
					material_reservation_id={reservation.id}
					setIsCutModalOpen={setIsCutModalOpen}
					refetch={() => {
						refetch();
					}}
				/>
			) : null}
		</>
	);
};
